/**
 * Sesión D (Iter 41.13) — Suites page redesigned for hm_advisor.
 *
 * 5 sections per suite card:
 *   1. Operational status badge (active / attention / pending / blocked)
 *   2. Client impact metrics (5 KPIs, all from real /admin/suites/overview)
 *   3. Advisor action items (derived from KPIs)
 *   4. AI insights preview (max 2, 60-char truncation, from dashboard alerts)
 *   5. Quick actions (open / create / review / launch / export)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2, AlertTriangle, Clock, Ban,
  Layers, FileSearch, FilePlus, Send, Download, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { api, formatApiError } from '@/lib/api';
import NewEvaluationDialog from '@/components/admin/NewEvaluationDialog';

const SUITE_ROUTE = {
  climara: '/admin/climara',
  talentmap: '/admin/talentmap',
  talentcheck: '/admin/talentcheck',
  talentstart: '/admin/talentstart',
  talentheart: '/admin/talentheart',
  talentfix: '/admin/talentfix',
  talentcompetency: '/admin/talentcompetency',
  talenttrain: '/admin/talenttrain',
  intelligence: '/admin/intelligence',
};

const STATUS_TONE = {
  active:    { badge: 'bg-success/15 text-success border-success/30',     icon: CheckCircle2, ring: 'ring-success/30' },
  attention: { badge: 'bg-warning/15 text-warning border-warning/30',     icon: AlertTriangle, ring: 'ring-warning/30' },
  pending:   { badge: 'bg-info/15 text-info border-info/30',              icon: Clock,         ring: 'ring-info/30' },
  blocked:   { badge: 'bg-destructive/15 text-destructive border-destructive/30', icon: Ban,  ring: 'ring-destructive/30' },
};

function deriveStatus(s) {
  // Iter 41.13 rule:
  //   blocked   → active_questionnaires == 0
  //   attention → reports_pending > 0 OR completion_pct < 60 (and total > 0)
  //   pending   → reports approved > released
  //   active    → otherwise
  if ((s.active_questionnaires || 0) === 0) return 'blocked';
  if ((s.reports_pending || 0) >= 3 || ((s.assignments_total || 0) > 0 && (s.completion_pct || 0) < 60)) {
    return 'attention';
  }
  if ((s.reports_approved || 0) > (s.reports_released || 0)) return 'pending';
  return 'active';
}

function deriveActions(s, t, alerts) {
  const items = [];
  if ((s.reports_pending || 0) > 0) {
    items.push({
      key: 'reportsPending',
      label: t('suites.actions.reportsPending', { count: s.reports_pending, n: s.reports_pending }),
      tone: 'warning',
    });
  }
  if ((s.assignments_total || 0) > 0 && (s.completion_pct || 0) < 60) {
    items.push({ key: 'low', label: t('suites.actions.lowParticipation'), tone: 'warning' });
  }
  if (s.code === 'talenttrain' && (s.reports_total || 0) > 0) {
    items.push({ key: 'train', label: t('suites.actions.trainingPending'), tone: 'info' });
  }
  if (s.code === 'climara' && alerts?.some((a) => /climate|clima/i.test(a))) {
    items.push({ key: 'climate', label: t('suites.actions.climateRisk'), tone: 'destructive' });
  }
  const stalled = Math.max(0, (s.assignments_in_progress || 0) - (s.assignments_submitted || 0));
  if (stalled >= 5) {
    items.push({ key: 'stalled', label: t('suites.actions.stalledAssignments', { count: stalled, n: stalled }), tone: 'warning' });
  }
  return items;
}

const ACTION_TONE = {
  warning: 'bg-warning/10 border-warning/30 text-warning',
  info: 'bg-info/10 border-info/30 text-info',
  destructive: 'bg-destructive/10 border-destructive/30 text-destructive',
};

const truncate = (s, n = 60) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : (s || ''));

function SuiteCard({ s, alerts, onCreate, t, i18n, navigate }) {
  const status = deriveStatus(s);
  const tone = STATUS_TONE[status];
  const Icon = tone.icon;
  const actions = deriveActions(s, t, alerts);
  const aiAlerts = (alerts || []).slice(0, 2).map((a) => truncate(a));
  const route = SUITE_ROUTE[s.code] || '/admin/suites';

  const onExport = async () => {
    try {
      // Quick-action #5: export a CSV summary of this suite's activity for assigned clients.
      const params = new URLSearchParams({ suite_code: s.code });
      const url = `${api.defaults.baseURL}/admin/suites/${s.code}/export.csv?${params.toString()}`;
      // Fall back to a structured CSV built client-side if the endpoint
      // doesn't exist yet (best-effort: ship the snapshot the user sees).
      const res = await api.get(`/admin/suites/${s.code}/export.csv`, {
        responseType: 'blob', validateStatus: () => true,
      });
      if (res.status === 200) {
        const blob = new Blob([res.data], { type: 'text/csv' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${s.code}-summary.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success(t('suites.qa.exportStarted'));
        return;
      }
      // Fallback: build CSV from the metrics we already have on the card.
      const csv = [
        ['suite_code', 'status', 'active_evaluations', 'reports_pending', 'completion_pct', 'avg_score', 'risk_alerts'],
        [s.code, status, s.assignments_in_progress || 0, s.reports_pending || 0, s.completion_pct || 0, s.avg_score || '', (alerts || []).length],
      ].map((r) => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${s.code}-summary.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(t('suites.qa.exportStarted'));
    } catch (e) {
      toast.error(t('suites.qa.exportFailed'), { description: formatApiError(e) });
    }
  };

  return (
    <Card className={`p-6 shadow-card space-y-5 ring-1 ${tone.ring}`} data-testid={`advisor-suite-card-${s.code}`}>
      {/* Header + Section 1: Operational status */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-bold text-lg tracking-tight">{s.name}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{s.tagline}</p>
        </div>
        <Badge variant="outline" className={`${tone.badge} gap-1 shrink-0 font-semibold`} data-testid={`advisor-suite-status-${s.code}`}>
          <Icon className="h-3 w-3" />
          {t(`suites.opStatus.${status}`)}
        </Badge>
      </div>

      {/* Section 2: Client impact metrics */}
      <div className="grid grid-cols-5 gap-2 text-center" data-testid={`advisor-suite-impact-${s.code}`}>
        <Metric n={s.assignments_in_progress || 0} label={t('suites.impact.activeEvals')} />
        <Metric n={s.reports_pending || 0} label={t('suites.impact.reportsPending')} tone={s.reports_pending > 0 ? 'warning' : null} />
        <Metric n={`${s.completion_pct || 0}%`} label={t('suites.impact.participation')} tone={s.completion_pct < 60 && s.assignments_total > 0 ? 'warning' : null} />
        <Metric n={s.avg_score != null ? s.avg_score : '—'} label={t('suites.impact.avgScore')} />
        <Metric n={(alerts || []).length} label={t('suites.impact.riskAlerts')} tone={(alerts || []).length > 0 ? 'destructive' : null} />
      </div>

      {/* Section 3: Advisor action items */}
      <div>
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
          {t('suites.actions.title')}
        </p>
        {actions.length === 0 ? (
          <div className="text-xs text-muted-foreground italic" data-testid={`advisor-suite-actions-clear-${s.code}`}>
            <span className="font-medium">{t('suites.actions.noneTitle')}</span> · {t('suites.actions.noneBody')}
          </div>
        ) : (
          <ul className="space-y-1">
            {actions.map((a) => (
              <li key={a.key} className={`text-xs px-2.5 py-1.5 rounded-md border ${ACTION_TONE[a.tone]}`} data-testid={`advisor-suite-action-${s.code}-${a.key}`}>
                {a.label}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Section 4: AI insights preview */}
      <div>
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2 flex items-center gap-1">
          <Sparkles className="h-3 w-3 text-primary" /> {t('suites.ai.title')}
        </p>
        {aiAlerts.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">{t('suites.ai.empty')}</p>
        ) : (
          <ul className="space-y-1">
            {aiAlerts.map((a, i) => (
              <li key={i} className="text-xs px-2.5 py-1.5 rounded-md bg-primary/5 border border-primary/15 text-foreground/80" data-testid={`advisor-suite-ai-${s.code}-${i}`}>
                {a}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Section 5: Quick actions */}
      <div className="grid grid-cols-2 gap-2 pt-1 border-t border-border">
        <Button variant="default" size="sm" className="gap-1.5" onClick={() => navigate(route)} data-testid={`advisor-suite-open-${s.code}`}>
          <Layers className="h-3.5 w-3.5" /> {t('suites.qa.open')}
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => onCreate(s.code)} data-testid={`advisor-suite-create-${s.code}`}>
          <FilePlus className="h-3.5 w-3.5" /> {t('suites.qa.create')}
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(`/admin/reports?suite=${s.code}`)} data-testid={`advisor-suite-review-${s.code}`}>
          <FileSearch className="h-3.5 w-3.5" /> {t('suites.qa.review')}
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(`/admin/questionnaires?suite=${s.code}`)} data-testid={`advisor-suite-launch-${s.code}`}>
          <Send className="h-3.5 w-3.5" /> {t('suites.qa.launch')}
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5 col-span-2" onClick={onExport} data-testid={`advisor-suite-export-${s.code}`}>
          <Download className="h-3.5 w-3.5" /> {t('suites.qa.export')}
        </Button>
      </div>
    </Card>
  );
}

const TONE_CLS = {
  warning: 'text-warning',
  destructive: 'text-destructive',
};

function Metric({ n, label, tone }) {
  return (
    <div>
      <p className={`text-xl font-bold tabular-nums ${tone ? TONE_CLS[tone] : ''}`}>{n}</p>
      <p className="text-xs uppercase tracking-wider text-muted-foreground mt-0.5 leading-tight">{label}</p>
    </div>
  );
}

export default function AdminSuitesAdvisor() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [suites, setSuites] = useState([]);
  const [alertsBySuite, setAlertsBySuite] = useState({});
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [seedSuite, setSeedSuite] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const ov = await adminApi.suitesOverview();
        if (alive) setSuites(ov?.suites || []);
        // Fetch dashboard alerts and bucket by suite_code (best-effort).
        try {
          const alerts = await api.get('/admin/dashboard-alerts/actions/all', { validateStatus: () => true }).then((r) => r.data);
          // Alerts API returns either action rows or alert seeds — we tolerate both.
          const byCode = {};
          (alerts || []).forEach((row) => {
            const code = row.suite_code || row.suite || '';
            const text = row.alert_message || row.message || row.title || row.title_en || row.alert_key || '';
            if (code && text) {
              byCode[code] = byCode[code] || [];
              if (byCode[code].length < 2) byCode[code].push(text);
            }
          });
          if (alive) setAlertsBySuite(byCode);
        } catch { /* alerts optional */ }
      } catch (e) {
        toast.error(t('suites.qa.exportFailed'), { description: formatApiError(e) });
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [t]);

  const cards = useMemo(() => suites, [suites]);

  return (
    <>
      <Topbar title={t('suites.advisorTitle')} subtitle={t('suites.advisorSubtitle')} />
      <div className="p-6 lg:p-8 space-y-6">
        {loading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
          {cards.map((s) => (
            <SuiteCard
              key={s.code}
              s={s}
              alerts={alertsBySuite[s.code] || []}
              onCreate={(code) => { setSeedSuite(code); setCreateOpen(true); }}
              t={t} i18n={i18n} navigate={navigate}
            />
          ))}
        </div>
      </div>
      <NewEvaluationDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaultSuiteCode={seedSuite}
        onCreated={() => { setCreateOpen(false); }}
      />
    </>
  );
}
